﻿import React, { useState, useEffect } from 'react';
import { SpectralCoefficient } from '../models/spectral-coefficient';
import { Document } from '../models/document';
import SingleDocumentLoader from './SingleDocumentLoader';
import { DocumentType, FtirAnalysisType } from '../models/enums';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { CrudFunctions } from '../utilities/crud-functions';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    LoadPanel as DataGridLoadPanel
} from 'devextreme-react/data-grid';

import { Toast } from 'devextreme-react/toast';

export interface Props {
    benchSheetId: string;
    ftirAnalysisType: FtirAnalysisType;
    isReadOnly: boolean;
    headerBgClass?: string;
    onDataReceived?: () => void;
}

export default function FtirSpectralCoefficientsData(props: Props) {
    const [spectralCoefficients, setSpectralCoefficients] =
        useState<SpectralCoefficient[]>();

    const [spectralCoefficientsDocument, setSpectralCoefficientsDocument] =
        useState<Document>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getAllData();
    }, [props.benchSheetId, props.ftirAnalysisType]);

    //tell parent that have data
    useEffect(() => {
        if (spectralCoefficients && props.onDataReceived)
            props.onDataReceived();
    }, [spectralCoefficients]);

    return (
        <div>
            <div className={`lq-header pw-margin-top-05 ${props.headerBgClass ? props.headerBgClass : 'pw-bg-rb-700'}`}>
                <div className="lq-header-title">Spectral Coefficients</div>
            </div>

            <SingleDocumentLoader readOnly={props.isReadOnly}
                documentName={spectralCoefficientsDocument ?
                    spectralCoefficientsDocument.name : ''}
                documentId={spectralCoefficientsDocument ?
                    spectralCoefficientsDocument.documentId : ''}
                onDocumentLoaded={onSpectralCoefficientsDocumentLoaded} />

            {spectralCoefficients &&
                spectralCoefficients.length > 0 &&
                <DataGrid
                    height="8rem"
                    allowColumnResizing={true}
                    wordWrapEnabled={true}
                    columnResizingMode="widget"
                    dataSource={spectralCoefficients ? spectralCoefficients : []}
                    rowAlternationEnabled={true}
                    showRowLines={true} 
                    showBorders={true}>

                    <DataGridLoadPanel enabled={true} />

                    <Column dataField={'correlation'} width={'6rem'} />
                    <Column dataField={'referenceTitle'} />
                    <Column dataField={'qCheckRegion'} width={'10rem'} />
                    <Column dataField={'pass'} width={'6rem'} cellRender={spectralCoefficientPassFail} />

                </DataGrid>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }

        </div>
    );

    async function onSpectralCoefficientsDocumentLoaded(spectralCoefficientsDocument: Document) {
        spectralCoefficientsDocument.parentId = props.benchSheetId;
        spectralCoefficientsDocument.documentType = getDocumentType();

        await insertSpectralCoefficientsDocument(spectralCoefficientsDocument)
            .then((data) => {
                // is this right?

                setSpectralCoefficients(data);
                setSpectralCoefficientsDocument(spectralCoefficientsDocument);

                //setBenchSheet({ ...benchSheet, spectralCoefficients: data, spectralCoefficientsDocument: spectralCoefficientsDocument })
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Inserting FTIR Spectral Coefficients Document");
            });
    }

    async function getFtirSpectralCoefficientsData() {
        await CrudFunctions.getData(`benchsheets/spectralcoefficientsbybenchsheetid/${props.benchSheetId}/${props.ftirAnalysisType}`)
            .then((data) => {
                setSpectralCoefficients(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving FTIR Spectral Coefficients Data");
            });
    }

    async function getFtirSpectralCoefficientsDocument() {
        await CrudFunctions.getData(`benchsheets/spectralcoefficientsdocumentbybenchsheetid/${props.benchSheetId}/${props.ftirAnalysisType}`)
            .then((data) => {

                console.log("getFtirSpectralCoefficientsDocument()");
                console.log(data);

                setSpectralCoefficientsDocument(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving FTIR Spectral Coefficients Document");
            });
    }

    async function insertSpectralCoefficientsDocument(spectralCoefficientsDocument: Document) {
        return await CrudFunctions.postData(`benchsheets/spectralcoefficientsfiledata/${props.benchSheetId}/${props.ftirAnalysisType}`,
            spectralCoefficientsDocument);
    }

    async function getAllData() {
        if (props.benchSheetId && props.ftirAnalysisType) {
            await Promise.all([
                getFtirSpectralCoefficientsData(),
                getFtirSpectralCoefficientsDocument()
            ]);
        }
    }

    //render

    function spectralCoefficientPassFail(e: any) {
        if (e.data && e.data.pass)
            return <div className="valid-data">Pass</div>;

        return <div className="invalid-data">Fail</div>;
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function getDocumentType() {
        switch (props.ftirAnalysisType) {
            case FtirAnalysisType.AsIs:
                return DocumentType.FtirAsIsSpectralCoefficient;
            case FtirAnalysisType.Recrystalized:
                return DocumentType.FtirRecrystalizedSpectralCoefficient;
            case FtirAnalysisType.Dried:
                return DocumentType.FtirDryingSpectralCoefficient;
            case FtirAnalysisType.Salt:
                return DocumentType.FtirSaltSpectralCoefficient;
        }
    }
}